import { Typography, alpha, useTheme } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import * as React from 'react';

import { invalidDatePickerUserMessage } from '../../utils/date-picker-invalid-date';
import { useTranslation } from '../../utils/translations';
import { colors } from '../theme/theme';

import { StyledDatePicker } from './CommonDatePickerStyles';

/** MUI X DatePicker validation reasons (v6); forwarded for parent forms. */
export type DatePickerValidationReason =
    | 'invalidDate'
    | 'disableFuture'
    | 'disablePast'
    | 'minDate'
    | 'maxDate'
    | 'shouldDisableDate'
    | 'shouldDisableMonth'
    | 'shouldDisableYear'
    | null;

const PICKER_ERROR_MESSAGE_KEYS: Partial<
    Record<Exclude<DatePickerValidationReason, null>, { dob: string; generic: string }>
> = {
    maxDate: { dob: 'datePicker.dateOfBirth.minAge18', generic: 'datePicker.generic.maxDate' },
    minDate: { dob: 'datePicker.dateOfBirth.maxAge120', generic: 'datePicker.generic.minDate' },
    disableFuture: { dob: 'datePicker.dateOfBirth.future', generic: 'datePicker.generic.disableFuture' },
    disablePast: { dob: 'datePicker.dateOfBirth.invalid', generic: 'datePicker.generic.disablePast' },
};

function getPickerErrorMessageKey(reason: Exclude<DatePickerValidationReason, null>, dob: boolean): string {
    const entry = PICKER_ERROR_MESSAGE_KEYS[reason] ?? {
        dob: 'datePicker.dateOfBirth.invalid',
        generic: 'datePicker.generic.invalid',
    };
    return dob ? entry.dob : entry.generic;
}

interface CommonDatePickerProps {
    label?: string;
    value: Dayjs | null;
    onChange: (newValue: Dayjs | null) => void;
    required?: boolean;
    disabled?: boolean;
    minDate?: Dayjs;
    maxDate?: Dayjs;
    DatePickerLabel?: string;
    placeholder?: string;
    DatePickerErrorValue?: string;
    /** When true, shows error state (red border) on the input. */
    error?: boolean;
    open?: boolean;
    onOpen?: () => void;
    onClose?: () => void;
    hideInput?: boolean;
    calenderIconColor?: string;
    ariaLabel?: string;
    /** When true, the input is read-only; user must use the date picker (no manual typing). */
    readOnly?: boolean;
    /** First view when opening the calendar (useful for DOB: year-first). */
    openTo?: 'day' | 'month' | 'year';
    views?: ('year' | 'month' | 'day')[];
    /**
     * Fired when the picker's internal validation state changes (manual typing, min/max, invalid date).
     * `value` is the value MUI evaluated (use when `reason === null` to avoid stale closure vs React state).
     */
    onPickerError?: (reason: DatePickerValidationReason, value: Dayjs | null, meta?: { rawInput: string }) => void;
    /** Which copy to use for built-in picker error text when `DatePickerErrorValue` is not set. */
    validationContext?: 'dateOfBirth' | 'generic';
}

const CommonDatePicker: React.FC<CommonDatePickerProps> = ({
    label,
    value,
    onChange,
    required = false,
    disabled = false,
    minDate,
    maxDate,
    DatePickerLabel,
    DatePickerErrorValue,
    error = false,
    open: controlledOpen,
    onOpen: controlledOnOpen,
    onClose: controlledOnClose,
    hideInput = false,
    calenderIconColor = disabled ? colors.greyPreset[10] : colors.fullBlack,
    ariaLabel,
    readOnly = false,
    openTo = 'day',
    views,
    onPickerError,
    validationContext = 'generic',
}) => {
    const { t } = useTranslation();
    const inputRef = React.useRef<HTMLInputElement>(null);
    const [internalOpen, setInternalOpen] = React.useState(false);
    const [, setMuiPickerError] = React.useState(false);
    const [pickerErrorMessage, setPickerErrorMessage] = React.useState('');
    const CustomCalendarIcon = () => (
        <span
            className="icon-calendar"
            style={{
                color: calenderIconColor,
                fontSize: '20px',
                display: 'inline-block',
                width: '20px',
                height: '20px',
            }}
        />
    );
    const theme = useTheme();
    // Use controlled state if provided, otherwise use internal state
    const isControlled = controlledOpen !== undefined;
    const open = isControlled ? controlledOpen : internalOpen;

    const handleOpen = () => {
        if (!disabled) {
            if (isControlled && controlledOnOpen) {
                controlledOnOpen();
            } else {
                setInternalOpen(true);
            }
        }
    };

    const handleClose = () => {
        if (isControlled && controlledOnClose) {
            controlledOnClose();
        } else {
            setInternalOpen(false);
        }
    };

    const handlePickerError = React.useCallback(
        (reason: DatePickerValidationReason, pickerValue: Dayjs | null) => {
            if (readOnly) return;
            const hasError = reason != null;
            // We intentionally don't surface MUI's internal helper/error copy.
            // Custom error copy is rendered below via `errorTxt`.
            setMuiPickerError(hasError);
            const dob = validationContext === 'dateOfBirth';
            const p = (key: string) => t(key);
            const rawFromInput = (inputRef.current?.value ?? '').trim();
            if (!reason) {
                setPickerErrorMessage('');
            } else if (reason === 'invalidDate') {
                setPickerErrorMessage(invalidDatePickerUserMessage(rawFromInput, validationContext, p));
            } else {
                setPickerErrorMessage(p(getPickerErrorMessageKey(reason, dob)));
            }
            onPickerError?.(reason, pickerValue, reason === 'invalidDate' ? { rawInput: rawFromInput } : undefined);
        },
        [readOnly, onPickerError, validationContext, t],
    );

    // Do not rely on MUI internal error state for the UI.
    // We render our own message via `resolvedErrorValue` and suppress MUI helper text.
    const showTextFieldError = Boolean(error) || Boolean(DatePickerErrorValue);
    const resolvedErrorValue = DatePickerErrorValue || pickerErrorMessage;

    /** MM/DD/YYYY is 10 chars; caps paste / overflow without changing MUI parsing rules. */
    const textFieldInputProps: React.InputHTMLAttributes<HTMLInputElement> = {
        maxLength: 10,
        ...(ariaLabel ? { 'aria-label': ariaLabel } : {}),
        ...(readOnly ? { readOnly: true } : {}),
    };

    return (
        <StyledDatePicker>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
                {DatePickerLabel && !hideInput && (
                    <Typography variant="subtitle2" className="textFieldTitle">
                        {DatePickerLabel}
                        {required && (
                            <Typography component="span" sx={{ color: colors.errorPreset[10], ml: 0.5 }}>
                                *
                            </Typography>
                        )}
                    </Typography>
                )}

                <DatePicker
                    value={value}
                    onChange={(newValue, context: { validationError?: DatePickerValidationReason }) => {
                        const nextValue = newValue as Dayjs | null;
                        const validationError = context?.validationError as DatePickerValidationReason | undefined;

                        // Always allow clearing the field.
                        if (nextValue === null) {
                            onChange(null);
                            return;
                        }

                        // Commit typed/picked value only when it is a valid, in-range date.
                        // This ensures manual entry follows the same min/max/past/future rules.
                        if (dayjs(nextValue).isValid() && !validationError) {
                            onChange(nextValue);
                        }
                    }}
                    format="MM/DD/YYYY"
                    minDate={minDate}
                    maxDate={maxDate}
                    openTo={openTo}
                    views={views}
                    open={open}
                    onOpen={() => {
                        // Block field-click auto-open; picker opens only via calendar icon button.
                    }}
                    onClose={handleClose}
                    onError={handlePickerError}
                    label={label}
                    desktopModeMediaQuery="@media (min-width:0px)"
                    slots={{
                        openPickerIcon: CustomCalendarIcon,
                    }}
                    slotProps={{
                        textField: {
                            inputRef,
                            error: showTextFieldError,
                            // MUI DatePicker may still generate its own helper text for min/max validation.
                            // Hide the helper-text area entirely; we render our own error message below.
                            helperText: '',
                            FormHelperTextProps: { style: { display: 'none' } },
                            inputProps: textFieldInputProps,
                            ...(readOnly && {
                                onKeyDown: (e: React.KeyboardEvent) => e.preventDefault(),
                            }),
                            // onClick: handleOpen,
                            sx: {
                                '& .MuiInputAdornment-root': {
                                    margin: 0,
                                },
                                '& .MuiIconButton-root': {
                                    padding: '5px',
                                },
                                ...(hideInput
                                    ? {
                                          '& .MuiOutlinedInput-root': {
                                              height: 0,
                                              minHeight: 0,
                                              padding: 0,
                                              margin: 0,
                                              opacity: 0,
                                              position: 'absolute',
                                              pointerEvents: 'none',
                                          },
                                          '& .MuiInputBase-input': {
                                              padding: 0,
                                              height: 0,
                                          },
                                      }
                                    : {
                                          '& .MuiOutlinedInput-root': {
                                              cursor: 'text',
                                          },
                                          '& .MuiInputBase-input': {
                                              cursor: 'text',
                                          },
                                      }),
                            },
                        },
                        openPickerButton: {
                            onClick: handleOpen,
                        },
                        popper: {
                            placement: 'bottom-start',
                            modifiers: [
                                {
                                    name: 'setWidth',
                                    enabled: true,
                                    phase: 'beforeWrite',
                                },
                                {
                                    name: 'preventOverflow',
                                    options: {
                                        boundary: 'viewport',
                                        padding: 8,
                                    },
                                },
                                {
                                    name: 'flip',
                                    options: {
                                        fallbackPlacements: ['top-start', 'bottom-start', 'top', 'bottom'],
                                    },
                                },
                            ],
                            sx: {
                                '& .MuiPaper-root': {
                                    borderRadius: '16px',
                                    boxShadow: '0px 4px 16px rgba(0,0,0,0.1)',
                                    padding: theme => theme.spacing(0),
                                    border: `1px solid ${colors.greyPreset[30]}`,
                                },
                                '& .MuiPickersDay-root': {
                                    ...theme.typography.body2,
                                    fontWeight: 500,
                                    padding: '9.5px 11px',
                                    '&.Mui-selected': {
                                        backgroundColor: `${colors.primaryLight[100]} !important`,
                                        border: `1px solid ${colors.primaryLight[100]}`,
                                        boxShadow: `0 0 0 1px ${colors.fullWhite} inset !important`,
                                        color: colors.fullWhite,
                                    },
                                    '&:hover': {
                                        backgroundColor: alpha(colors.primaryLight[100], 0.2),
                                    },
                                    '&.MuiPickersDay-today': {
                                        border: `1px solid ${colors.primaryLight[100]}`,
                                        bgcolor: colors.greyPreset[20],
                                    },
                                },
                                '& .MuiPickersYear-yearButton': {
                                    '&.Mui-selected': {
                                        backgroundColor: `${colors.primaryLight[100]} !important`,
                                        border: `1px solid ${colors.primaryLight[100]}`,
                                        boxShadow: `0 0 0 1px ${colors.fullWhite} inset !important`,
                                        color: colors.fullWhite,
                                    },
                                    '&:hover': {
                                        backgroundColor: alpha(colors.primaryLight[100], 0.2),
                                    },
                                },
                                '& .MuiDateCalendar-root': {
                                    width: 296,
                                    '& .MuiPickersFadeTransitionGroup-root': {
                                        px: 1,
                                    },
                                    '& .MuiPickersCalendarHeader-label': {
                                        ...theme.typography.body2,
                                        fontWeight: 400,
                                    },
                                    '& .MuiButtonBase-root': {
                                        '& svg': {
                                            color: colors.fullBlack,
                                        },
                                        '&:disabled': {
                                            opacity: 0.4,
                                            cursor: 'not-allowed',
                                            '& svg': {
                                                color: colors.greyPreset[10],
                                            },
                                        },
                                    },
                                    '& .MuiDayCalendar-header': {
                                        '& .MuiTypography-root': {
                                            color: colors.fullBlack,
                                        },
                                    },
                                },
                                '& .MuiYearCalendar-root': {
                                    width: 300,
                                    maxHeight: 270,
                                },
                            },
                        },
                    }}
                />
                {resolvedErrorValue && (
                    <Typography variant="subtitle2" className="errorTxt">
                        {resolvedErrorValue}
                    </Typography>
                )}
            </LocalizationProvider>
        </StyledDatePicker>
    );
};

export default CommonDatePicker;
